import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/cn";

/**
 * Soft-tint pill for labels (category, type, status).
 *
 * Paired with <Badge> (@/components/ui/badge): Badge = solid emphasis
 * (counts, notifications); Tag = tinted label. Keep them distinct —
 * if a use case needs a solid fill, use Badge; for a soft tinted chip, Tag.
 */
const tagVariants = cva(
  "inline-flex items-center gap-1 rounded-full py-0.5 font-semibold whitespace-nowrap",
  {
    variants: {
      tone: {
        primary: "bg-primary/10 text-primary",
        destructive: "bg-destructive/10 text-destructive",
        success: "bg-success/15 text-success",
        warning: "bg-warning/15 text-warning",
        neutral: "bg-muted text-foreground/60",
        info: "bg-info/15 text-info",
      },
      size: {
        sm: "px-2 text-3xs",
        md: "px-2.5 text-xs",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "md",
    },
  },
);

export interface TagProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof tagVariants> {}

export function Tag({ className, tone, size, ...props }: TagProps) {
  return <span className={cn(tagVariants({ tone, size }), className)} {...props} />;
}
